import { UrgencyReason } from "@clipboard-health/contract-backend-main";
import { isDefined } from "@clipboard-health/util-ts";
import { ShiftCancellationReasonTypes } from "@src/appV2/ShiftLogs/api/ShiftCancellationReasonTypes";
import {
  type ComputedArrival,
  type ShiftWithAdjustments,
  UrgencyType,
} from "@src/appV2/Shifts/types";
import { differenceInMinutes, isAfter, isFuture, parseISO } from "date-fns";

import { type SentHomeInfo } from "./parseSentHome";
import { SHIFT_STATES, type ShiftCardData, type ShiftState } from "./shiftCardTypes";

// extendBookabilityTo is the only field cleared when a search is explicitly
// stopped (POST /shift/unassign, requestReplacement: false) — bookable/
// requestReplacement are left stale on that path.
export function isBookabilityWindowActive(extendBookabilityTo: string | undefined): boolean {
  return isDefined(extendBookabilityTo) && isFuture(parseISO(extendBookabilityTo));
}

interface ReplacementSearchFields {
  bookable?: boolean;
  extendBookabilityTo?: string;
  urgentlyBooked?: boolean;
}

export function isReplacementSearchOpen(shift: ReplacementSearchFields): boolean {
  return (
    shift.bookable !== false &&
    isBookabilityWindowActive(shift.extendBookabilityTo) &&
    shift.urgentlyBooked !== true
  );
}

/**
 * Whether the shift's urgency indicates an active (still-open) replacement
 * search. This is the shared signal behind both the "Urgent" chip
 * (`isUrgentShift`) and the card's "Finding a replacement..." subtitle, so the
 * two can never disagree.
 *
 * A WORKER_REPLACEMENT_REQUESTED (facility-reported) shift is only searching
 * while its bookability window is open — that urgencyReason lingers after the
 * search concludes, so it needs the full `isReplacementSearchOpen` gate. NCNS /
 * LATE_CANCELLATION urgency (a worker cancellation) still requires the shift to
 * be open — `bookable !== false` (not closed / worker returned) and not already
 * `urgentlyBooked` (refilled) — but bypasses the future-`extendBookabilityTo`
 * window, which the backend does not reliably keep fresh on that path.
 *
 * Does not consider whether the shift has ended or was deleted; callers handle
 * that (the card via `hasEnded`, the chip via its own guards).
 */
export function isUrgentReplacementSearch(
  shift: ReplacementSearchFields & { urgency?: number; urgencyReason?: string | null }
): boolean {
  if (shift.urgencyReason === UrgencyReason.WORKER_REPLACEMENT_REQUESTED) {
    return isReplacementSearchOpen(shift);
  }

  return (
    (shift.urgency === UrgencyType.NCNS || shift.urgency === UrgencyType.LATE_CANCELLATION) &&
    shift.bookable !== false &&
    shift.urgentlyBooked !== true
  );
}

/**
 * Whether the worker is still physically on-site for the shift.
 *
 * Mirrors the backend's `isWorkerOnSiteForShift` / `agentIsInTheFacility`
 * (shift-timekeeping/utils/agent-is-in-facility.ts): the worker must have
 * arrived, and then the departure check prefers the continuous distance-based
 * `isWorkerFarFromFacility` signal, which is resilient to the geofence flag
 * flipping momentarily on GPS drift near the boundary. Falls back to the
 * `leftFacilityGeofence` flag only when the distance signal is absent (older
 * shifts, or shifts where ETA tracking never started).
 */
export function isWorkerStillOnSite(attendance: ShiftWithAdjustments["attendance"]): boolean {
  if (!isDefined(attendance?.arrivedFacilityGeofenceAt)) {
    return false;
  }

  if (isDefined(attendance.isWorkerFarFromFacility)) {
    return !attendance.isWorkerFarFromFacility;
  }

  return attendance.leftFacilityGeofence !== true;
}

/**
 * Computes the state of a shift based on its data and sent home info.
 * Applies state priority rules.
 *
 * Note: NO_SHOW_SUSPECTED and NO_SHOW_REPLACED states are set in
 * `applyReplacementPairStates()` after cross-shift pair detection.
 *
 * @param now - Current time, injectable for testing. Defaults to new Date().
 */
export function computeShiftState(
  shift: ShiftWithAdjustments,
  sentHomeInfo?: SentHomeInfo,
  computedArrival?: ComputedArrival,
  now?: Date
): ShiftState {
  const currentTime = now ?? new Date();

  if (sentHomeInfo) {
    return SHIFT_STATES.SENT_HOME;
  }

  if (shift.workerLeftEarlyRequest) {
    return SHIFT_STATES.WORKER_LEFT_EARLY;
  }

  const hasWorker = Boolean(shift.agent?.userId);

  if (!hasWorker) {
    return SHIFT_STATES.UNFILLED;
  }

  if (shift.clockInOut?.end) {
    return SHIFT_STATES.CLOCKED_OUT;
  }

  if (shift.clockInOut?.start) {
    return SHIFT_STATES.CLOCKED_IN;
  }

  // In building: worker is still on-site (arrival is checked inside
  // isWorkerStillOnSite) and shift has started (clock-in already ruled out
  // above). Departure clears IN_BUILDING so the card no longer shows a worker
  // who has left the facility.
  const shiftStart = parseISO(shift.start);
  if (isWorkerStillOnSite(shift.attendance) && isAfter(currentTime, shiftStart)) {
    return SHIFT_STATES.IN_BUILDING;
  }

  // Running late is a pre-arrival prediction from the booking-time ETA. Once the
  // worker has arrived that prediction is stale, so only apply it before arrival
  // — otherwise an arrived-then-departed worker would flip back to RUNNING_LATE.
  const hasArrived = isDefined(shift.attendance?.arrivedFacilityGeofenceAt);
  if (!hasArrived && computedArrival) {
    const targetArrival = parseISO(computedArrival.targetArrivalTime);
    const computedShiftStart = parseISO(computedArrival.shiftStartAt);
    if (isAfter(targetArrival, computedShiftStart)) {
      return SHIFT_STATES.RUNNING_LATE;
    }
  }

  return SHIFT_STATES.SCHEDULED;
}

/**
 * Gets the most recent computed arrival for the assigned worker.
 * Matches by workerId, bookedAt, and shiftStartAt to handle rebooking scenarios.
 * Uses findLast to get the latest matching arrival if multiple exist.
 */
export function getComputedArrivalForWorker(
  shift: ShiftWithAdjustments
): ComputedArrival | undefined {
  const workerId = shift.agent?.userId;
  const { computedArrivals, bookedAt, start } = shift;

  if (!computedArrivals?.length || !workerId) {
    return undefined;
  }

  if (bookedAt) {
    return computedArrivals.findLast(
      (arrival) =>
        arrival.workerId === workerId &&
        arrival.bookedAt === bookedAt &&
        arrival.shiftStartAt === start
    );
  }

  return computedArrivals.findLast((arrival) => arrival.workerId === workerId);
}

/**
 * Extracts sent-home info from a shift's workerLeftEarly field.
 * Only returns SentHomeInfo when `leftWithPermission` is true (worker was sent home by facility).
 * When `leftWithPermission` is false, the worker left on their own and should use the
 * WORKER_LEFT_EARLY state instead (handled by computeShiftState's workerLeftEarly check).
 *
 * Note: eventAt is not available from workerLeftEarly - it only exists in shift logs.
 */
export function getSentHomeInfoFromShift(shift: ShiftWithAdjustments): SentHomeInfo | undefined {
  if (!shift.workerLeftEarlyRequest?.leftWithPermission) {
    return undefined;
  }

  return {
    shiftId: shift._id,
    reason: ShiftCancellationReasonTypes.SENT_HOME_BY_FACILITY,
  };
}

/**
 * Detects replacement shift pairs and applies NO_SHOW states.
 *
 * A replacement shift has `replacedShiftId` pointing to the original shift.
 * When the replacement is an NCNS replacement (urgencyLevel === NCNS):
 * - NO_SHOW_SUSPECTED: replacement is unfilled (no worker assigned)
 * - NO_SHOW_REPLACED: replacement has a worker assigned
 *
 * Also sets `previousWorker` on replacement cards from the original shift data
 * when not already set from `previouslyAssignedWorker`.
 */
export function applyReplacementPairStates(cards: ShiftCardData[]): void {
  const cardsByShiftId = new Map(cards.map((card) => [card.shiftId, card]));

  for (const card of cards) {
    if (!card.replacedShiftId) {
      continue;
    }

    const originalCard = cardsByShiftId.get(card.replacedShiftId);

    if (!card.previousWorker && originalCard) {
      if (
        originalCard.workerId &&
        originalCard.workerName &&
        originalCard.workerId !== card.workerId
      ) {
        // NCNS: original still shows the no-show worker as current assignee
        card.previousWorker = {
          userId: originalCard.workerId,
          name: originalCard.workerName,
        };
      } else if (originalCard.previousWorker) {
        // Late cancellation: original worker was unassigned, leaving previousWorker on the original
        card.previousWorker = originalCard.previousWorker;
      }
    }

    // Set previousWorkerLabel for LATE_CANCELLATION / LAST_MINUTE replacements
    // (NCNS uses the NO_SHOW states which render via DailyViewNoShowShiftRowContent)
    if (
      card.previousWorker &&
      !card.previousWorkerLabel &&
      (card.urgencyLevel === UrgencyType.LATE_CANCELLATION ||
        card.urgencyLevel === UrgencyType.LAST_MINUTE)
    ) {
      card.previousWorkerLabel = "Canceled";
    }

    // Note, UrgencyType.NCNS is just a bad naming, it doesn't identify the reason of an urgent shift.
    // It just defines how soon we need a replacement.
    // UrgencyReason.ATTENDANCE_TRACKING_ALGO is the closest definition of NCNS.
    if (
      card.urgencyLevel === UrgencyType.NCNS &&
      card.urgencyReason === UrgencyReason.ATTENDANCE_TRACKING_ALGO
    ) {
      card.state = card.workerId ? SHIFT_STATES.NO_SHOW_REPLACED : SHIFT_STATES.NO_SHOW_SUSPECTED;
    }

    // Compute refill time for filled replacement shifts
    if (card.workerId && originalCard?.deletedAt && card.createdAt) {
      const cancelledTime = parseISO(originalCard.deletedAt);
      const bookedTime = parseISO(card.createdAt);
      const minutes = differenceInMinutes(bookedTime, cancelledTime);
      if (minutes >= 0) {
        card.refilledInMinutes = minutes;
      }
    }
  }
}
